import React, { useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Button from "@react/shared/ui/Button"
import BankGuaranteeModal from "./BankGuaranteeModal"
import { fmt, dateFmt } from "../helpers/format"

const ActiveBlock = ({ bankGuarantee, canEdit, onOpen }) => (
  <div className="bg-blue-50/60 border border-blue-200 rounded-xl px-4 py-3 flex flex-wrap items-center gap-4">
    <div className="flex items-center gap-2 shrink-0">
      <span className="w-8 h-8 grid place-items-center rounded-full bg-blue-100 text-blue-700">
        <FontAwesomeIcon icon="shield-halved" />
      </span>
      <div className="flex flex-col">
        <span className="text-[10.5px] uppercase tracking-wider font-medium text-blue-700">
          {I18n.t("projects.bank_guarantee.banner.eyebrow")}
        </span>
        <span className="text-[13px] font-semibold text-slate-900">
          {bankGuarantee.bank_name || I18n.t("projects.bank_guarantee.banner.unknown_bank")}
        </span>
      </div>
    </div>

    {bankGuarantee.reference && (
      <div className="flex flex-col">
        <span className="text-[10.5px] uppercase tracking-wider font-medium text-blue-700/80">
          {I18n.t("projects.bank_guarantee.banner.reference")}
        </span>
        <span className="text-[13px] font-mono text-slate-800">{bankGuarantee.reference}</span>
      </div>
    )}

    {bankGuarantee.amount?.cents > 0 && (
      <div className="flex flex-col">
        <span className="text-[10.5px] uppercase tracking-wider font-medium text-blue-700/80">
          {I18n.t("projects.bank_guarantee.banner.amount")}
        </span>
        <span className="text-[14px] font-semibold text-slate-900 tabular-nums">{fmt(bankGuarantee.amount)}</span>
      </div>
    )}

    {bankGuarantee.expires_on && (
      <div className="flex flex-col">
        <span className="text-[10.5px] uppercase tracking-wider font-medium text-blue-700/80">
          {I18n.t("projects.bank_guarantee.banner.expires_on")}
        </span>
        <span className="text-[13px] text-slate-800">{dateFmt(bankGuarantee.expires_on)}</span>
      </div>
    )}

    <div className="ml-auto flex items-center gap-3">
      <span className="text-[11.5px] text-slate-500">
        {I18n.t("projects.bank_guarantee.banner.replaces_retention")}
      </span>
      {canEdit && (
        <Button variant="ghost" size="sm" onClick={onOpen}>
          <FontAwesomeIcon icon="pen" /> {I18n.t("projects.bank_guarantee.banner.edit")}
        </Button>
      )}
    </div>
  </div>
)

const InactiveBlock = ({ canEdit, onOpen }) => (
  <div className="bg-white border border-slate-200 rounded-xl px-4 py-2 flex items-center gap-3 text-[12.5px] text-slate-500">
    <span className="w-6 h-6 grid place-items-center rounded-full bg-slate-100 text-slate-500">
      <FontAwesomeIcon icon="shield-halved" className="w-3 h-3" />
    </span>
    <span>{I18n.t("projects.bank_guarantee.banner.inactive_hint")}</span>
    {canEdit && (
      <button
        type      = "button"
        onClick   = {onOpen}
        className = "ml-auto text-[12px] text-blue-600 hover:text-blue-700 inline-flex items-center gap-1"
      >
        {I18n.t("projects.bank_guarantee.banner.activate")}
      </button>
    )}
  </div>
)

const BankGuaranteeBanner = ({ bankGuarantee, policies }) => {
  const [modalOpen, setModalOpen] = useState(false)
  if (!bankGuarantee) return null
  const canEdit = !!policies?.can_edit_bank_guarantee
  const projectId = bankGuarantee.update_url?.match(/\/projects\/(\d+)\//)?.[1]

  return (
    <div className="px-7 mt-3">
      {bankGuarantee.enabled
        ? <ActiveBlock   bankGuarantee={bankGuarantee} canEdit={canEdit} onOpen={() => setModalOpen(true)} />
        : <InactiveBlock canEdit={canEdit} onOpen={() => setModalOpen(true)} />}
      <BankGuaranteeModal
        isOpen        = {modalOpen}
        projectId     = {projectId}
        bankGuarantee = {bankGuarantee}
        onClose       = {() => setModalOpen(false)}
      />
    </div>
  )
}

export default BankGuaranteeBanner
